import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Instagram,
  Facebook,
  Youtube,
  Send,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BOOKING_URL = "https://link.sweatsetter.com/widget/booking/0bvqp14e0y6pNvAdcqsM";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/ynu3s4od_contact.jpg";

const interestOptions = [
  'Large Group Training',
  'Semi-Private Training',
  'Personal Training',
  'Nutrition Coaching',
  'HYROX Training',
  'General Question'
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInterestChange = (value) => {
    setFormData(prev => ({ ...prev, interest: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.interest || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.status === 'success') {
        setIsSubmitted(true);
        toast.success('Message sent successfully!');
        setFormData({
          name: '',
          email: '',
          phone: '',
          interest: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section - Full Width Image */}
      <section 
        className="relative flex items-center justify-center"
        style={{ height: '735px', marginTop: '0' }}
        data-testid="contact-hero"
        aria-label="Contact hero"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${HERO_IMAGE})`,
            top: '135px',
            height: '600px'
          }}
        >
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 container-custom text-center" style={{ marginTop: '135px' }}>
          <div className="max-w-3xl mx-auto">
            {/* Text overlay box with semi-transparent background */}
            <div className="bg-black/30 p-[50px] inline-block">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading text-white mb-6">
                Get in Touch
              </h1>
              <p className="text-lg text-white/90 mb-4">
                Have questions about our programs, HYROX training, schedule, or getting started?
              </p>
              <p className="text-white/80 mb-8">
                We would love to help.
              </p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  data-testid="contact-hero-cta"
                  className="bg-[#FF5A3C] hover:bg-[#E64526] text-white rounded-none font-nav uppercase tracking-widest px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  Book a Free Intro
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="section-padding bg-white" data-testid="contact-form-section" aria-label="Contact information and form">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-heading text-[#1A1A1A] mb-8">
                X-Team Fitness
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-none bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#FF5A3C]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] mb-1">Address</h3>
                    <p className="text-gray-600">
                      2928 West Leigh Street<br />
                      Richmond, VA 23230
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-none bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#FF5A3C]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] mb-1">Phone</h3>
                    <a href="tel:804-920-8086" className="text-gray-600 hover:text-[#FF5A3C] transition-colors">
                      804-920-8086
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-none bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#FF5A3C]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] mb-1">Email</h3>
                    <a href="mailto:info@xteamfitness.com" className="text-gray-600 hover:text-[#FF5A3C] transition-colors">
                      info@xteamfitness.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-none bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#FF5A3C]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] mb-1">Hours</h3>
                    <p className="text-gray-600">By class schedule and appointment</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="font-semibold text-[#1A1A1A] mb-4">Follow Along</h3>
                <div className="flex gap-4">
                  <a 
                    href="https://instagram.com/xteamfitness" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-none bg-[#F5F7FA] flex items-center justify-center hover:bg-[#FF5A3C] hover:text-white text-gray-600 transition-all duration-300"
                    data-testid="social-link-instagram"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                  <a 
                    href="https://facebook.com/xteamfitness" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-none bg-[#F5F7FA] flex items-center justify-center hover:bg-[#FF5A3C] hover:text-white text-gray-600 transition-all duration-300"
                    data-testid="social-link-facebook"
                  >
                    <Facebook className="w-6 h-6" />
                  </a>
                  <a 
                    href="https://youtube.com/xteamfitness" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-none bg-[#F5F7FA] flex items-center justify-center hover:bg-[#FF5A3C] hover:text-white text-gray-600 transition-all duration-300"
                    data-testid="social-link-youtube"
                  >
                    <Youtube className="w-6 h-6" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[#F5F7FA] rounded-none p-8">
              <h2 className="text-2xl font-heading text-[#1A1A1A] mb-2">
                Let's Talk About Your Goals
              </h2>
              <p className="text-gray-600 mb-6">
                Fill out the form below and our team will get back to you soon.
              </p>

              {isSubmitted ? (
                <div className="text-center py-12" data-testid="contact-success-message">
                  <div className="w-20 h-20 rounded-none bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-heading text-[#1A1A1A] mb-4">Message Sent!</h3>
                  <p className="text-gray-600 mb-6">
                    Thank you for reaching out. We'll be in touch soon.
                  </p>
                  <Button 
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="border-2 border-gray-200 hover:border-[#FF5A3C] hover:text-[#FF5A3C] rounded-none font-nav uppercase tracking-widest px-6 py-3"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="text-sm font-semibold text-gray-700 mb-2 block">
                      Name <span className="text-[#FF5A3C]">*</span>
                    </Label>
                    <Input 
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="w-full px-4 py-3 rounded-none border border-gray-200 bg-white focus:border-[#FF5A3C] focus:ring-2 focus:ring-[#FF5A3C]/20"
                      data-testid="contact-input-name"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-sm font-semibold text-gray-700 mb-2 block">
                      Email <span className="text-[#FF5A3C]">*</span>
                    </Label>
                    <Input 
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 rounded-none border border-gray-200 bg-white focus:border-[#FF5A3C] focus:ring-2 focus:ring-[#FF5A3C]/20"
                      data-testid="contact-input-email"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-sm font-semibold text-gray-700 mb-2 block">
                      Phone
                    </Label>
                    <Input 
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 555-5555"
                      className="w-full px-4 py-3 rounded-none border border-gray-200 bg-white focus:border-[#FF5A3C] focus:ring-2 focus:ring-[#FF5A3C]/20"
                      data-testid="contact-input-phone"
                    />
                  </div>

                  <div>
                    <Label htmlFor="interest" className="text-sm font-semibold text-gray-700 mb-2 block">
                      What are you interested in? <span className="text-[#FF5A3C]">*</span>
                    </Label>
                    <Select onValueChange={handleInterestChange} value={formData.interest}>
                      <SelectTrigger 
                        className="w-full px-4 py-3 rounded-none border border-gray-200 bg-white focus:border-[#FF5A3C] focus:ring-2 focus:ring-[#FF5A3C]/20"
                        data-testid="contact-select-interest"
                      >
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent>
                        {interestOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-sm font-semibold text-gray-700 mb-2 block">
                      Message <span className="text-[#FF5A3C]">*</span>
                    </Label>
                    <Textarea 
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="How can we help you?"
                      rows={5}
                      className="w-full px-4 py-3 rounded-none border border-gray-200 bg-white focus:border-[#FF5A3C] focus:ring-2 focus:ring-[#FF5A3C]/20 resize-none"
                      data-testid="contact-textarea-message"
                      required
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="w-full bg-[#FF5A3C] hover:bg-[#E64526] text-white rounded-none font-nav uppercase tracking-widest py-4 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-[#F5F7FA]" data-testid="map-section" aria-label="Location map">
        <div className="container-custom py-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-heading text-[#1A1A1A] mb-4">
              How to Find Us
            </h2>
            <p className="text-gray-600">
              Come see where your next chapter starts.
            </p>
          </div>
        </div>
        
        <div className="w-full">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3162.7!2d-77.47!3d37.55!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b1115c0e7b5e5f%3A0x1234567890abcdef!2s2928%20W%20Leigh%20St%2C%20Richmond%2C%20VA%2023230!5e0!3m2!1sen!2sus!4v1234567890"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="X-Team Fitness Location"
            className="w-full"
            data-testid="google-map-embed"
          />
        </div>
        
        <div className="container-custom py-8">
          <p className="text-center text-gray-600">
            Conveniently located in Scott's Addition with access to both indoor training and outdoor Richmond workouts.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-gradient-to-br from-[#FF5A3C] to-[#E64526] text-white" data-testid="contact-final-cta" aria-label="Call to action">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading mb-6">
            Ready to Take the First Step?
          </h2>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button 
              data-testid="contact-final-cta-btn"
              className="bg-white text-[#FF5A3C] hover:bg-gray-100 rounded-none font-nav uppercase tracking-widest px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              Book a Free Intro
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
